import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { useOutletContext } from 'react-router';
import { api, unwrap } from '../api/client';
import { useAgents, useCatalog } from '../api/hooks';
import { ErrorNote, Loading, PageHead, formatDate, formatDateTime } from '../components/ui';

interface MeContext {
  roles: Array<{ role: string }>;
  user: { id: string };
}

type LeavePayload = { startsOn?: string; endsOn?: string; absenceTypeId?: string; comment?: string | null };

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, [string, string]> = {
    pending: ['b-warn', 'En attente'],
    approved: ['b-ok', 'Approuvée'],
    rejected: ['b-bad', 'Refusée'],
    cancelled: ['b-min', 'Annulée'],
  };
  const [cls, label] = map[status] ?? ['b-min', status];
  return <span className={`b ${cls}`}>{label}</span>;
}

export function RequestsPage() {
  const me = useOutletContext<MeContext>();
  const roles = new Set(me.roles.map((r) => r.role));
  const canDecide = roles.has('chef_service') || roles.has('drh');
  const isDrh = roles.has('drh');
  const qc = useQueryClient();

  const mine = useQuery({ queryKey: ['requests'], queryFn: async () => unwrap(await api.GET('/api/v1/requests')) });
  const inbox = useQuery({ queryKey: ['requests', 'inbox'], queryFn: async () => unwrap(await api.GET('/api/v1/requests/inbox')), enabled: canDecide });
  const types = useCatalog('absence-types');
  const agents = useAgents();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['requests'] });
    void qc.invalidateQueries({ queryKey: ['audit'] });
  };

  const submit = useMutation({
    mutationFn: async (body: { agentId?: string; absenceTypeId: string; startsOn: string; endsOn: string; comment?: string | null }) =>
      unwrap(await api.POST('/api/v1/requests/leave', { body })),
    onSuccess: () => {
      invalidate();
      dialog.current?.close();
    },
  });
  const decide = useMutation({
    mutationFn: async ({ id, decision, comment }: { id: string; decision: 'approved' | 'rejected'; comment?: string }) =>
      unwrap(await api.POST('/api/v1/requests/{id}/decide', { params: { path: { id } }, body: { decision, comment: comment ?? null } })),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: async (id: string) => unwrap(await api.POST('/api/v1/requests/{id}/cancel', { params: { path: { id } } })),
    onSuccess: invalidate,
  });

  const dialog = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState({ agentId: '', absenceTypeId: '', startsOn: '', endsOn: '', comment: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate({
      agentId: isDrh && form.agentId ? form.agentId : undefined,
      absenceTypeId: form.absenceTypeId,
      startsOn: form.startsOn,
      endsOn: form.endsOn,
      comment: form.comment || null,
    });
  };

  const typeLabel = (id: unknown) => types.data?.find((t) => t.id === id)?.label ?? '…';
  const agentName = (id: string) => {
    const a = agents.data?.find((x) => x.id === id);
    return a ? `${a.lastName.toUpperCase()} ${a.firstName}` : '…';
  };
  const payload = (p: unknown) => p as LeavePayload;
  const todayIso = new Date().toISOString().slice(0, 10);

  const Table = ({ rows, actions }: { rows: NonNullable<typeof mine.data>; actions: (r: NonNullable<typeof mine.data>[number]) => React.ReactNode }) => (
    <div className="tw">
      <table>
        <thead>
          <tr>
            <th>Agent</th>
            <th>Type</th>
            <th>Du</th>
            <th>Au</th>
            <th>Déposée</th>
            <th>État</th>
            <th>Avertissements</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="empty">
                Aucune demande.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{agentName(r.agentId)}</td>
              <td>{typeLabel(payload(r.payload).absenceTypeId)}</td>
              <td>{formatDate(payload(r.payload).startsOn)}</td>
              <td>{formatDate(payload(r.payload).endsOn)}</td>
              <td className="small">{formatDateTime(r.submittedAt)}</td>
              <td>
                <StatusBadge status={r.status} />
              </td>
              <td className="small muted">{(Array.isArray(r.warnings) ? (r.warnings as string[]) : []).join(' · ') || '—'}</td>
              <td className="actions">{actions(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <PageHead
        eyebrow="Congés et absences"
        title="Demandes"
        actions={
          <button className="btn btn-primary" onClick={() => dialog.current?.showModal()}>
            Nouvelle demande
          </button>
        }
      >
        Chaque demande suit le circuit de son service. Les garde-fous bloquants sont vérifiés au dépôt.
      </PageHead>

      {canDecide && (
        <section className="stack" style={{ marginBottom: 24 }}>
          <h2>À valider par moi</h2>
          <ErrorNote error={inbox.error ?? decide.error} />
          {inbox.isPending ? (
            <Loading />
          ) : (
            <Table
              rows={inbox.data ?? []}
              actions={(r) => (
                <>
                  <button className="btn btn-sm btn-primary" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, decision: 'approved' })}>
                    Approuver
                  </button>
                  <button
                    className="btn btn-sm"
                    disabled={decide.isPending}
                    onClick={() => {
                      const comment = window.prompt('Motif du refus (visible par l’agent)') ?? '';
                      if (comment.trim()) decide.mutate({ id: r.id, decision: 'rejected', comment });
                    }}
                  >
                    Refuser
                  </button>
                </>
              )}
            />
          )}
        </section>
      )}

      <section className="stack">
        <h2>{roles.has('drh') || roles.has('elu') ? 'Toutes les demandes' : roles.has('chef_service') ? 'Demandes de mon périmètre' : 'Mes demandes'}</h2>
        <ErrorNote error={mine.error ?? cancel.error} />
        {mine.isPending ? (
          <Loading />
        ) : (
          <Table
            rows={mine.data ?? []}
            actions={(r) =>
              ['pending', 'approved'].includes(r.status) && (payload(r.payload).startsOn ?? '') > todayIso && (r.submittedBy === me.user.id || isDrh) ? (
                <button className="btn btn-sm" disabled={cancel.isPending} onClick={() => window.confirm('Annuler cette demande ?') && cancel.mutate(r.id)}>
                  Annuler
                </button>
              ) : null
            }
          />
        )}
      </section>

      <dialog ref={dialog} aria-labelledby="leave-title">
        <form onSubmit={onSubmit}>
          <h2 id="leave-title">Nouvelle demande de congé</h2>
          {isDrh && (
            <div className="field">
              <label htmlFor="l-agent">Agent (DRH : laisser vide pour soi-même)</label>
              <select id="l-agent" value={form.agentId} onChange={set('agentId')}>
                <option value="">— Moi-même —</option>
                {(agents.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.lastName.toUpperCase()} {a.firstName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor="l-type">Type d'absence</label>
            <select id="l-type" required value={form.absenceTypeId} onChange={set('absenceTypeId')}>
              <option value="">— Choisir —</option>
              {(types.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="l-start">Du</label>
              <input id="l-start" type="date" required value={form.startsOn} onChange={set('startsOn')} />
            </div>
            <div className="field">
              <label htmlFor="l-end">Au (inclus)</label>
              <input id="l-end" type="date" required value={form.endsOn} onChange={set('endsOn')} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="l-comment">Commentaire</label>
            <textarea id="l-comment" value={form.comment} onChange={set('comment')} style={{ minHeight: 64 }} />
          </div>
          <ErrorNote error={submit.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={submit.isPending}>
              Déposer
            </button>
            <button type="button" className="btn" onClick={() => dialog.current?.close()}>
              Annuler
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}
